/**
 * Pure helper for restoring the tool-timeout recovery card across a page
 * reload — mirrors lib/handoff-timeout-restore.ts's outstandingTimeoutOffer
 * exactly (same "which offer, if any, is still outstanding" shape), factored
 * out of page.tsx the same way so the rule is unit-tested without mounting
 * the page.
 *
 * Why this exists at all (see the `toolTimeoutOffer` doc comment on Message,
 * types/chat.ts): a `tool_timeout_offer` marker is an OUTSTANDING OFFER — a
 * live, actionable card — not a historical receipt. It must render as the
 * SAME ToolTimeoutCard page.tsx already mounts for a live timeout, not a
 * second inline copy of the card. This helper resolves which offer (if any)
 * that card should be mounted for on a restored conversation.
 */
import {
  ComponentType,
  MessageRole,
  type Message,
  type ToolTimeoutOfferState,
} from '@/types/chat';

/** Every tool name the backend's timeout envelope can carry (see
 *  agent/manager.py's _MCP_TOOL_TIMEOUT_DIRECTIVES + its default branch),
 *  mapped to the card kind a successful retry renders. Used ONLY to
 *  recognize "a later successful result for the SAME tool landed" (the
 *  `fulfilled` rule below); an unrecognized tool name simply can never be
 *  fulfilled by this branch (still fulfillable by a support-request
 *  receipt). search_aol_content is the unified retrieval tool (P3) — missing
 *  from this map until 2026-07-28, which left a restored offer for it
 *  permanently unfulfillable by a successful retry (stale card after
 *  reload). */
const TOOL_RESULT_KIND: Record<string, ComponentType> = {
  find_courses: ComponentType.COURSE_BROWSER,
  browse_aol_content: ComponentType.RAG_CONTENT,
  fetch_quick_aol_content: ComponentType.RAG_CONTENT,
  search_aol_content: ComponentType.RAG_CONTENT,
};

/**
 * The tool-timeout offer of the LAST restored `tool_timeout_offer` marker in
 * the transcript, unless it has since been resolved — in which case there is
 * nothing outstanding to restore and this returns undefined. Only the LAST
 * offer is ever considered: once a later offer exists (a fresh timeout, on
 * the same or a different tool), an earlier one is definitionally over.
 *
 * The last offer counts as resolved when, LATER in the transcript (index-
 * wise):
 *   (a) a `support_request_submitted` marker with NO `handoffId` appears —
 *       this flow normally submits with no handoff_id at all (unlike the
 *       live-agent timeout flow, whose receipts always carry one), so the
 *       absence of handoffId is itself the signal that a tool-timeout
 *       capture (not an unrelated handoff capture) fulfilled this offer; or
 *   (b) a tool-result message matching the offer's OWN tool (find_courses →
 *       COURSE_BROWSER, browse_aol_content → RAG_CONTENT — see
 *       TOOL_RESULT_KIND) appears — the retry succeeded and produced a
 *       renderable card.
 *
 * Known gap in (b): a retry that succeeds with an EMPTY result produces no
 * renderable card at all (mapPersistedResultToMessage's own empty-payload
 * skip), so it leaves no trace this rule can see — the offer would
 * (harmlessly) resurface after a reload in that narrow case. The backend
 * persists no explicit "this retry resolved" marker to close that gap.
 */
export function outstandingToolTimeoutOffer(
  messages: readonly Message[]
): ToolTimeoutOfferState | undefined {
  let lastOfferIndex = -1;
  let lastOffer: ToolTimeoutOfferState | undefined;

  messages.forEach((m, index) => {
    if (m.role === MessageRole.SYSTEM && m.toolTimeoutOffer) {
      lastOfferIndex = index;
      lastOffer = m.toolTimeoutOffer;
    }
  });

  if (lastOfferIndex === -1 || !lastOffer) return undefined;

  const later = messages.slice(lastOfferIndex + 1);

  const fulfilledByCapture = later.some(
    (m) =>
      m.role === MessageRole.SYSTEM &&
      m.supportRequestSubmitted &&
      !m.supportRequestSubmitted.handoffId
  );
  if (fulfilledByCapture) return undefined;

  const matchingKind = TOOL_RESULT_KIND[lastOffer.toolName];
  const fulfilledByRetrySuccess =
    matchingKind !== undefined &&
    later.some((m) => m.result?.kind === matchingKind);
  if (fulfilledByRetrySuccess) return undefined;

  return lastOffer;
}

/**
 * "Live wins over restored" — the SAME resolution rule page.tsx has always
 * applied for ToolTimeoutCard (a live ChatContext.toolTimeoutOffer is either
 * set this session, or it isn't; once set it stays set for the session — see
 * the SESSION_CHANGED clear in useAssistantConnection.ts — so its mere
 * presence is the whole "prefer live" rule), extracted so the voice-call
 * surface's CallToolTimeoutPanel (mounted from voice/call-screen.tsx) can
 * apply the identical rule without re-deriving it.
 *
 * RESTORED-OFFER RETRY DECISION (session-recovery retry_id-correlation
 * follow-on): a restored offer's `retryId` names a `pending_tool_retry`
 * entry that lives ONLY in the backend process's memory for the session
 * that timed out — never persisted, and never rehydrated on reconnect (see
 * ToolRetryOutcomeState's doc comment: outcomes are live-only). A fresh
 * connection — which is exactly what "restored after a reload" means —
 * ALWAYS starts with an empty pending_tool_retry for its (new) session, so
 * handle_tool_retry's "no pending entry" guard rejects a retry_tool_call
 * carrying a restored retryId 100% of the time, silently (no outcome is
 * ever sent for a retry_id the backend was never told about — see that
 * guard's own docstring). This isn't a race or an edge case to warn about;
 * it is a GUARANTEE, so this forces `retryAvailable: false` on a
 * restored-only offer rather than presenting a "Try again" that is
 * mathematically certain to spin forever. The offer itself, and "Leave my
 * details", both still work identically — only the retry affordance is
 * suppressed, the SAME "hide entirely, don't disable" treatment the
 * backend's own retry-cap already uses (see ToolTimeoutFailed/
 * ToolTimeoutOffer's `retryAvailable` prop).
 */
export function resolveEffectiveToolTimeoutOffer(
  live: ToolTimeoutOfferState | null,
  messages: readonly Message[]
): ToolTimeoutOfferState | undefined {
  if (live) return live;
  const restored = outstandingToolTimeoutOffer(messages);
  if (!restored) return undefined;
  return { ...restored, retryAvailable: false };
}
